import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/quotationApi";
import { useBranding } from "@/lib/branding";
import FloatingSaveBar from "@/components/FloatingSaveBar";
import PaymentPlansManager from "@/components/admin/PaymentPlansManager";


const VALIDITY_PRESETS = [7, 15, 30];

export default function QuotationSettings() {
  const { branding, refresh } = useBranding();
  const [validity, setValidity] = useState(String(branding.validity_days ?? 15));
  const [customMode, setCustomMode] = useState(
    !VALIDITY_PRESETS.includes(Number(branding.validity_days ?? 15)),
  );
  const [footerNote, setFooterNote] = useState(branding.footer_note || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const v = Number(branding.validity_days ?? 15);
    setValidity(String(v));
    setCustomMode(!VALIDITY_PRESETS.includes(v));
    setFooterNote(branding.footer_note || "");
  }, [branding]);

  const save = async () => {
    const days = parseInt(validity, 10);
    if (!Number.isFinite(days) || days < 1 || days > 365) {
      toast.error("Validity must be between 1 and 365 days");
      return;
    }
    setSaving(true);
    try {
      await api.put(
        `/settings/branding`,
        {
          validity_days: days,
          footer_note: footerNote.trim(),
        },
      );
      await refresh();
      toast.success("Quotation settings updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card-luxe space-y-6" data-testid="quotation-settings">
      <div>
        <div className="overline text-[#C5A880] mb-2">Quotation</div>
        <p className="text-white/50 text-sm">
          Tune how every quotation closes out. Shown on the final summary, the
          public page, the PDF, and the WhatsApp share message.
        </p>
      </div>

      {/* Validity */}
      <div>
        <label className="label-luxe">Validity period</label>
        <div className="grid grid-cols-4 gap-2">
          {VALIDITY_PRESETS.map((d) => {
            const selected = !customMode && Number(validity) === d;
            return (
              <button
                key={d}
                type="button"
                onClick={() => {
                  setCustomMode(false);
                  setValidity(String(d));
                }}
                className="rounded-lg border py-2.5 text-sm font-display font-semibold transition"
                style={{
                  borderColor: selected ? "var(--primary)" : "rgba(255,255,255,0.10)",
                  background: selected ? "color-mix(in srgb, var(--primary) 12%, transparent)" : "rgba(255,255,255,0.02)",
                  color: selected ? "var(--primary)" : "rgba(255,255,255,0.7)",
                }}
                data-testid={`validity-preset-${d}`}
              >
                {d} days
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => setCustomMode(true)}
            className="rounded-lg border py-2.5 text-sm font-display font-semibold transition"
            style={{
              borderColor: customMode ? "var(--primary)" : "rgba(255,255,255,0.10)",
              background: customMode ? "color-mix(in srgb, var(--primary) 12%, transparent)" : "rgba(255,255,255,0.02)",
              color: customMode ? "var(--primary)" : "rgba(255,255,255,0.7)",
            }}
            data-testid="validity-preset-custom"
          >
            Custom
          </button>
        </div>
        {customMode && (
          <div className="mt-3">
            <label className="label-luxe">Custom validity (days)</label>
            <div className="grid grid-cols-[1fr_auto] gap-3 items-center">
              <input
                type="number"
                inputMode="numeric"
                min="1"
                max="365"
                className="input-luxe"
                value={validity}
                onChange={(e) => setValidity(e.target.value)}
                placeholder="e.g. 45"
                data-testid="validity-custom-input"
              />
              <div className="rounded-lg border border-[#C5A880]/30 bg-[#C5A880]/5 px-3 py-2 text-[11px] text-[#C5A880] whitespace-nowrap">
                {parseInt(validity, 10) > 0 ? `${validity} days` : "—"}
              </div>
            </div>
          </div>
        )}
        <p className="text-[11px] text-white/40 mt-2">
          Appears at the bottom of every customer-facing quotation.
        </p>
      </div>

      {/* Footer note */}
      <div>
        <label className="label-luxe">Quotation footer note (optional)</label>
        <textarea
          className="input-luxe !h-32 !py-3 leading-snug resize-none"
          value={footerNote}
          onChange={(e) => setFooterNote(e.target.value)}
          placeholder="e.g. All prices inclusive of installation and 1-year warranty. Taxes extra as applicable."
          maxLength={600}
          rows={5}
          data-testid="footer-note-input"
        />
        <div className="text-[10px] text-white/30 text-right mt-1">
          {footerNote.length}/600
        </div>
        <p className="text-[11px] text-white/40 mt-1.5">
          Free-form text printed at the very end of every quotation, just under
          the validity notice.
        </p>
      </div>

      {/* Floats above PaymentPlansManager's own bar (which sits at bottom-0). */}
      <FloatingSaveBar
        onSave={save}
        saving={saving}
        label="Save quotation settings"
        savingLabel="Saving…"
        testId="quotation-settings-save-btn"
        bottomOffset={68}
      />

      <PaymentPlansManager />
    </div>
  );
}
